// Red counts on the coach sidebar show only what is new: items that arrived since the user last
// had that menu open. The ids seen are kept per user and menu in this browser's localStorage
// (as purchaseSeen.js does for Purchase); without it every item simply counts as new.
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'coachMenuSeen';
const CHANGE_EVENT = 'coach-menu-seen-change';
// Ids kept per menu, oldest dropped first, so the store can't grow without end.
const MAX_SEEN = 1000;

const readStore = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
        return {};
    }
};

const userKey = () => localStorage.getItem('userId') || 'guest';

const readUser = () => readStore()[userKey()] || {};

const idsOf = (ids) => [...new Set(ids.filter((id) => id !== undefined && id !== null && id !== '').map(String))];

// How many of `ids` haven't been seen yet.
export const countNew = (seenIds = [], ids = []) => {
    const seen = new Set(seenIds);
    return idsOf(ids).filter((id) => !seen.has(id)).length;
};

// Everything the menu shows is seen once it is open. Ids seen before stay seen, so an item
// that drops out of the list (another date or Balgruha) doesn't come back as new.
export const markMenuSeen = (menu, ids = []) => {
    const store = readStore();
    const mine = store[userKey()] || {};
    const previous = mine[menu] || [];
    const known = new Set(previous);
    const fresh = idsOf(ids).filter((id) => !known.has(id));
    if (!fresh.length) return;

    store[userKey()] = { ...mine, [menu]: [...previous, ...fresh].slice(-MAX_SEEN) };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
        return;
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
};

// { menu: seen ids } for the signed-in user, updated whenever a menu is marked seen
// (also from another tab).
export function useMenuSeen() {
    const [seen, setSeen] = useState(readUser);

    useEffect(() => {
        const refresh = () => setSeen(readUser());
        refresh();
        window.addEventListener(CHANGE_EVENT, refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener(CHANGE_EVENT, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, []);

    return seen;
}
